import {
  FACTORY_CLOSED_STATUSES,
  csvDocument,
  csvLine,
  findMajor,
  type FactoryStatus,
  type FactoryTaxonomyKey,
  type FactoryView,
} from '@repo/shared'
import { FACTORY_STATUS_LABEL } from './quote-labels'

/**
 * 관리자 공장 목록의 거르기와 상세 확인 메일 대상 내보내기 (대표 결정 10/30: 전체를 넣고 파트너만 필터로 추린다).
 * 화면(pages/factories.tsx)이 쓰고, 시험(tests/mobile/admin-factory-filters.test.ts)이 결과를 본다
 */
export interface FactoryFilter {
  /** 이름·지역·품목·메모 글자 */
  q: string
  /** '' = 모든 단계 */
  status: FactoryStatus | ''
  /** 'product:sauce' 꼴. '' = 모든 분류 */
  major: string
  region: string
  certification: string
  /** 분류 확인 필요만 */
  needsReview: boolean
  /** 파트너만 (빠른 필터) */
  partnerOnly: boolean
}

export const EMPTY_FACTORY_FILTER: FactoryFilter = {
  q: '',
  status: '',
  major: '',
  region: '',
  certification: '',
  needsReview: false,
  partnerOnly: false,
}

const has = (text: string, needle: string) =>
  !needle.trim() || text.toLowerCase().includes(needle.trim().toLowerCase())

export const majorKeyOf = (k: Pick<FactoryTaxonomyKey, 'axis' | 'majorId'>) =>
  `${k.axis}:${k.majorId}`

export function filterFactories(list: readonly FactoryView[], f: FactoryFilter): FactoryView[] {
  return list.filter(
    (x) =>
      has([x.name, x.region, x.items, x.notes].join(' '), f.q) &&
      (!f.status || x.status === f.status) &&
      (!f.partnerOnly || x.status === 'partner') &&
      (!f.major || x.taxonomyKeys.some((k) => majorKeyOf(k) === f.major)) &&
      has(x.region, f.region) &&
      has(x.certifications.join(' '), f.certification) &&
      (!f.needsReview || x.taxonomyNeedsReview),
  )
}

/** '정보 비어 있음' 조건. 상세 확인 메일로 채울 칸 */
export const MISSING_INFO = {
  moq: { label: '최소 주문량', missing: (f: FactoryView) => !f.moqNote },
  certifications: { label: '인증', missing: (f: FactoryView) => f.certifications.length === 0 },
  taxonomy: {
    label: '분류',
    missing: (f: FactoryView) => f.taxonomyKeys.length === 0 || f.taxonomyNeedsReview,
  },
  leadTime: { label: '기간', missing: (f: FactoryView) => !f.leadTimeNote },
} as const
export type MissingInfoKey = keyof typeof MISSING_INFO

/**
 * 상세 확인 메일 대상: 거른 목록에서 중단·연락 금지를 늘 빼고(정보통신망법 50조, 발송 제외 목록), 연락 수단(대표 메일·문의폼)이
 * 있는 공장만. missing 을 고르면 그중 하나라도 비어 있는 공장만
 */
export function mailTargets(
  list: readonly FactoryView[],
  missing: readonly MissingInfoKey[],
): FactoryView[] {
  return list.filter(
    (f) =>
      !FACTORY_CLOSED_STATUSES.includes(f.status) &&
      (f.email || f.contactFormUrl) &&
      (missing.length === 0 || missing.some((k) => MISSING_INFO[k].missing(f))),
  )
}

/** 분류 키 → '소스 / 메뉴 디저트' 같은 한 줄 */
export const factoryTaxonomyText = (keys: readonly FactoryTaxonomyKey[]) =>
  keys
    .map(
      (k) => `${k.axis === 'menu' ? '메뉴 ' : ''}${findMajor(k.axis, k.majorId)?.ko ?? k.majorId}`,
    )
    .join(' / ')

/** 메일 대상 CSV (엑셀용). 발송은 하지 않는다 — 대표 승인 뒤 이 파일로 보낸다 */
export function mailTargetsCsv(rows: readonly FactoryView[]): string {
  return csvDocument([
    csvLine([
      '공장',
      '단계',
      '분류',
      '대표 메일',
      '문의폼',
      '대표번호',
      '홈페이지',
      '비어 있는 정보',
    ]),
    ...rows.map((f) =>
      csvLine([
        f.name,
        FACTORY_STATUS_LABEL[f.status],
        factoryTaxonomyText(f.taxonomyKeys),
        f.email,
        f.contactFormUrl,
        f.phone,
        f.website,
        (Object.keys(MISSING_INFO) as MissingInfoKey[])
          .filter((k) => MISSING_INFO[k].missing(f))
          .map((k) => MISSING_INFO[k].label)
          .join(' · '),
      ]),
    ),
  ])
}
